// src/pages/CompanySettings.jsx
// Manager-only: edit business profile, bank accounts, office locations
// Feeds pdfData.corporate / pdfData.bank / pdfData.addresses used by quotePdfGenerator
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Company } from '../services/api';
import { useAuth } from '../context/AuthContext';

const EMPTY_CORP = { business: '', address: '', phone: '', email: '', website: '', gst: '', logo_url: '' };
const EMPTY_BANK = { id: 0, branch: '', Detail: '' };
const EMPTY_ADDR = { id: 0, office: '', address: '' };

export default function CompanySettings() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // ── Guard: managers only ──────────────────────────────────
  useEffect(() => {
    if (user.ryts !== 1 && user.ryts !== '1') navigate('/');
  }, [user, navigate]);

  const [tab, setTab] = useState('profile'); // profile | bank | locations
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState('');

  const [corp, setCorp] = useState(EMPTY_CORP);
  const [saving, setSaving] = useState(false);

  const [banks, setBanks] = useState([]);
  const [bankModal, setBankModal] = useState(false);
  const [bankForm, setBankForm] = useState(EMPTY_BANK);
  const [bankSaving, setBankSaving] = useState(false);

  const [addresses, setAddresses] = useState([]);
  const [addrModal, setAddrModal] = useState(false);
  const [addrForm, setAddrForm] = useState(EMPTY_ADDR);
  const [addrSaving, setAddrSaving] = useState(false);

  const flashSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  const load = useCallback(() => {
  setBusy(true); setErr('');
  Company.get()
    .then(d => {
      if (d) {
        setCorp({ ...EMPTY_CORP, ...d });
        setBanks(Array.isArray(d.bank?.data) ? d.bank.data : []);
        setAddresses(Array.isArray(d.address?.data) ? d.address.data : []);
      }
    })
    .catch(ex => setErr(ex.message))
    .finally(() => setBusy(false));
}, []);
  useEffect(load, [load]);

  // ── Company profile ───────────────────────────────────────
  const setCorpField = k => e => setCorp(c => ({ ...c, [k]: e.target.value }));

  const saveCorp = async () => {
    if (!corp.business) { setErr('Business name is required'); return; }
    setSaving(true); setErr('');
    try {
      await Company.update({ ...corp, corp_id: user.corp_id });
      flashSuccess('Company profile updated');
    } catch (ex) { setErr(ex.message); }
    finally { setSaving(false); }
  };

  // ── Bank details ───────────────────────────────────────────
  const openAddBank  = () => { setBankForm(EMPTY_BANK); setBankModal(true); setErr(''); };
  const openEditBank = (b) => { setBankForm(b); setBankModal(true); setErr(''); };
  const setBankField = k => e => setBankForm(f => ({ ...f, [k]: e.target.value }));

  const saveBank = async () => {
    if (!bankForm.branch || !bankForm.Detail) { setErr('Branch and account details are required'); return; }
    setBankSaving(true); setErr('');
    try {
      await Company.saveBank({ ...bankForm, corp_id: user.corp_id });
      setBankModal(false);
      load();
      flashSuccess(bankForm.id ? 'Bank account updated' : 'Bank account added');
    } catch (ex) { setErr(ex.message); }
    finally { setBankSaving(false); }
  };

  const removeBank = async (id) => {
    if (!window.confirm('Remove this bank account?')) return;
    try { await Company.deleteBank(id); load(); flashSuccess('Bank account removed'); }
    catch (ex) { setErr(ex.message); }
  };

  // ── Office locations ────────────────────────────────────────
  const openAddAddr  = () => { setAddrForm(EMPTY_ADDR); setAddrModal(true); setErr(''); };
  const openEditAddr = (a) => { setAddrForm(a); setAddrModal(true); setErr(''); };
  const setAddrField = k => e => setAddrForm(f => ({ ...f, [k]: e.target.value }));

  const saveAddr = async () => {
    if (!addrForm.office || !addrForm.address) { setErr('Office name and address are required'); return; }
    setAddrSaving(true); setErr('');
    try {
      await Company.saveAddress({ ...addrForm, corp_id: user.corp_id });
      setAddrModal(false);
      load();
      flashSuccess(addrForm.id ? 'Location updated' : 'Location added');
    } catch (ex) { setErr(ex.message); }
    finally { setAddrSaving(false); }
  };

  const removeAddr = async (id) => {
    if (!window.confirm('Remove this location?')) return;
    try { await Company.deleteAddress(id); load(); flashSuccess('Location removed'); }
    catch (ex) { setErr(ex.message); }
  };

  if (busy) return <div className="spinner-wrap"><div className="spinner" /></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title">🏢 Company Settings</div>
      </div>

      {err && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{err}</div>}
      {success && <div className="alert" style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: 8, padding: '8px 12px', marginBottom: '1rem', fontSize: '.85rem' }}>{success}</div>}

      <div className="tabs" style={{ marginBottom: '1.2rem' }}>
        <button className={`tab${tab === 'profile'   ? ' active' : ''}`} onClick={() => setTab('profile')}>Company Profile</button>
        <button className={`tab${tab === 'bank'       ? ' active' : ''}`} onClick={() => setTab('bank')}>Bank Details ({banks.length})</button>
        <button className={`tab${tab === 'locations'  ? ' active' : ''}`} onClick={() => setTab('locations')}>Locations ({addresses.length})</button>
      </div>

      {/* ── COMPANY PROFILE ── */}
      {tab === 'profile' && (
        <div className="card" style={{ maxWidth: 560 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.2rem' }}>
            {corp.logo_url ? (
              <img src={corp.logo_url} alt="Company logo" style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--border)', background: '#fff' }} />
            ) : (
              <div style={{ width: 64, height: 64, borderRadius: 8, border: '1px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--faint)', fontSize: '1.5rem' }}>🏢</div>
            )}
            <label style={{ flex: 1, marginBottom: 0 }}>
              Logo URL
              <input value={corp.logo_url} onChange={setCorpField('logo_url')} placeholder="https://…/logo.png" />
            </label>
          </div>

          <label style={{ marginBottom: '1rem' }}>
            Business Name *
            <input value={corp.business} onChange={setCorpField('business')} />
          </label>
          <label style={{ marginBottom: '1rem' }}>
            Registered Address
            <textarea rows={2} value={corp.address} onChange={setCorpField('address')} />
          </label>
          <div className="form-row" style={{ marginBottom: '1rem' }}>
            <label>Phone<input value={corp.phone} onChange={setCorpField('phone')} /></label>
            <label>Email<input type="email" value={corp.email} onChange={setCorpField('email')} /></label>
          </div>
          <div className="form-row" style={{ marginBottom: '1rem' }}>
            <label>Website<input value={corp.website} onChange={setCorpField('website')} /></label>
            <label>GST No.<input value={corp.gst} onChange={setCorpField('gst')} /></label>
          </div>
          <button className="btn btn-primary" onClick={saveCorp} disabled={saving}>
            {saving ? 'Saving…' : 'Save Profile'}
          </button>
        </div>
      )}

      {/* ── BANK DETAILS ── */}
      {tab === 'bank' && (
        <>
          <button className="btn btn-primary" style={{ marginBottom: '1rem' }} onClick={openAddBank}>+ Add Bank Account</button>
          {banks.length === 0 ? <div className="empty">No bank accounts added yet</div> :
            banks.map((b, i) => (
              <div key={b.id || i} className="list-row" style={{ marginBottom: '.5rem' }}>
                <div className="row-main">
                  <div className="row-title">{b.branch}</div>
                  <div className="row-sub" style={{ whiteSpace: 'pre-line' }}>{b.Detail}</div>
                </div>
                <div className="row-actions">
                  <button className="btn btn-sm btn-ghost" onClick={() => openEditBank(b)}>Edit</button>
                  <button className="btn btn-sm btn-danger" onClick={() => removeBank(b.id)}>Remove</button>
                </div>
              </div>
            ))
          }
        </>
      )}

      {/* ── LOCATIONS ── */}
      {tab === 'locations' && (
        <>
          <button className="btn btn-primary" style={{ marginBottom: '1rem' }} onClick={openAddAddr}>+ Add Location</button>
          {addresses.length === 0 ? <div className="empty">No office locations added yet</div> :
            addresses.map((a, i) => (
              <div key={a.id || i} className="list-row" style={{ marginBottom: '.5rem' }}>
                <div className="row-main">
                  <div className="row-title">{a.office}</div>
                  <div className="row-sub">{a.address}</div>
                </div>
                <div className="row-actions">
                  <button className="btn btn-sm btn-ghost" onClick={() => openEditAddr(a)}>Edit</button>
                  <button className="btn btn-sm btn-danger" onClick={() => removeAddr(a.id)}>Remove</button>
                </div>
              </div>
            ))
          }
        </>
      )}

      {/* ── Bank modal ── */}
      {bankModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setBankModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{bankForm.id ? 'Edit Bank Account' : 'Add Bank Account'}</span>
              <button className="btn-icon" onClick={() => setBankModal(false)}>✕</button>
            </div>
            {err && <div className="alert alert-error">{err}</div>}
            <label style={{ marginBottom: '1rem' }}>
              Branch / Bank Name *
              <input value={bankForm.branch} onChange={setBankField('branch')} placeholder="e.g. HDFC Bank, MG Road Branch" />
            </label>
            <label style={{ marginBottom: '1.2rem' }}>
              Account Details *
              <textarea rows={3} value={bankForm.Detail} onChange={setBankField('Detail')}
                placeholder={'A/C No: 1234567890\nIFSC: HDFC0001234\nA/C Name: Your Company Pvt Ltd'} />
            </label>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={saveBank} disabled={bankSaving}>{bankSaving ? 'Saving…' : 'Save'}</button>
              <button className="btn btn-ghost" onClick={() => setBankModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Location modal ── */}
      {addrModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setAddrModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{addrForm.id ? 'Edit Location' : 'Add Location'}</span>
              <button className="btn-icon" onClick={() => setAddrModal(false)}>✕</button>
            </div>
            {err && <div className="alert alert-error">{err}</div>}
            <label style={{ marginBottom: '1rem' }}>
              Office Name *
              <input value={addrForm.office} onChange={setAddrField('office')} placeholder="e.g. Head Office, Mumbai" />
            </label>
            <label style={{ marginBottom: '1.2rem' }}>
              Full Address *
              <textarea rows={2} value={addrForm.address} onChange={setAddrField('address')} />
            </label>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={saveAddr} disabled={addrSaving}>{addrSaving ? 'Saving…' : 'Save'}</button>
              <button className="btn btn-ghost" onClick={() => setAddrModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}